import React, { useEffect, useState } from 'react';
import { stockService, ProductListItem, ProductType } from '../src/services/stockService';

// Preserve the existing rental/LOA rules, while explaining excluded references.
export function machineUnavailableReason(product: ProductListItem): string | undefined {
  if (product.numero_serie) {
    return ({ SOLD: 'Vendue', MAINTENANCE: 'En maintenance', LOA: 'Déjà en LOA' } as Record<string, string>)[product.status];
  }
  return (product.stock_total ?? 0) > 0 ? undefined : 'Aucun stock';
}

interface ProductPickerProps {
  value: string;
  onSelect: (product: ProductListItem) => void;
  type?: ProductType;
  machineOnly?: boolean;
  wholesale?: boolean;
  dailyRate?: boolean;
  disabled?: boolean;
}

export function ProductPicker({ value, onSelect, type, machineOnly, wholesale, dailyRate, disabled }: ProductPickerProps) {
  const [query, setQuery] = useState('');
  const [request, setRequest] = useState({ search: '', page: 1 });
  const [result, setResult] = useState<{ data: ProductListItem[]; total: number; pages: number }>({ data: [], total: 0, pages: 0 });
  const [selected, setSelected] = useState<ProductListItem>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const pendingSearch = query.trim() !== request.search;

  useEffect(() => {
    const timer = setTimeout(() => setRequest({ search: query.trim(), page: 1 }), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    stockService.listStock({ page: request.page, page_size: 20, search: request.search, type })
      .then(response => {
        if (!cancelled) setResult({ data: response.data, total: response.pagination.total_items, pages: response.pagination.total_pages });
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Chargement impossible');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, type, retry]);

  const busy = loading || pendingSearch;
  return (
    <div className="space-y-2 text-sm">
      <input aria-label="Rechercher un article" type="search" value={query}
        onChange={event => setQuery(event.target.value)} disabled={disabled}
        placeholder="Désignation, référence, numéro de série…"
        className="w-full border border-slate-300 rounded-md px-3 py-2" />
      {value && selected?.id === value && (
        <p className="text-green-700">Sélection : {selected.reference} — {selected.designation}</p>
      )}
      {error ? (
        <div role="alert" className="text-red-700">
          Impossible de charger les articles : {error}.{' '}
          <button type="button" className="underline" onClick={() => setRetry(n => n + 1)}>Réessayer</button>
        </div>
      ) : busy ? <p role="status">Chargement des articles…</p> : (
        <>
          <div className="max-h-48 overflow-y-auto space-y-1" aria-label="Articles">
            {result.data.length === 0 && <p>Aucun article trouvé.</p>}
            {result.data.map(product => {
              const reason = machineOnly ? machineUnavailableReason(product) : undefined;
              const price = dailyRate ? product.tarif_location_journalier : wholesale ? product.prix_vente_grossiste : product.prix_vente_public;
              return (
                <button key={product.id} type="button" disabled={disabled || !!reason}
                  aria-pressed={value === product.id}
                  onClick={() => { setSelected(product); onSelect(product); }}
                  className={`w-full text-left border rounded-md p-2 disabled:opacity-50 ${value === product.id ? 'border-green-600 bg-green-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <span className="block font-medium">{product.reference} — {product.designation}</span>
                  <span className="text-xs text-slate-600">
                    {reason || `${new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(price)}${dailyRate ? ' /j' : ''}`}
                    {product.numero_serie && ` • N° ${product.numero_serie}`}
                    {!reason && product.status !== 'AVAILABLE' && ` • ${product.status}`}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2">
            <button type="button" disabled={disabled || request.page <= 1} onClick={() => setRequest(r => ({ ...r, page: r.page - 1 }))}>Précédent</button>
            <span>{result.total} articles • Page {request.page} / {Math.max(1, result.pages)}</span>
            <button type="button" disabled={disabled || request.page >= result.pages} onClick={() => setRequest(r => ({ ...r, page: r.page + 1 }))}>Suivant</button>
          </div>
        </>
      )}
    </div>
  );
}
